/**
 * Samlad nedladdning: "Ladda ner alla dokument".
 *
 * Kör projektets befintliga Word-generatorer i tur och ordning, fångar upp
 * filerna via exporthistorikens capture-läge och paketerar allt i en ZIP med
 * mappstruktur per skede plus underlag (JSON/CSV).
 */
import JSZip from "jszip";
import { supabase } from "@/integrations/supabase/client";
import { collectProjectBundle, rowsToCsv } from "./projectBundle";
import { trackUsage } from "@/lib/usage/track";
import {
  beginExportCapture,
  endExportCapture,
  type CapturedExportFile,
} from "@/lib/documentGenerator/exportHistory";
import { generateSamRoutineWordDocument } from "@/lib/documentGenerator/samRoutineWordGenerator";
import { generateSKRWordDocument } from "@/lib/documentGenerator/skrWordGenerator";
import { generateAmpWordDocument } from "@/lib/documentGenerator/ampWordGenerator";
import { generateArbetsberedningWordDocument } from "@/lib/documentGenerator/arbetsberedningWordGenerator";
import { generateSafetyRoundWordDocument } from "@/lib/documentGenerator/skyddsrondWordGenerator";
import { generateIncidentReportWordDocument } from "@/lib/documentGenerator/incidentReportWordGenerator";
import { generateUsePhaseFollowupWordDocument } from "@/lib/documentGenerator/bruksuppfoljningWordGenerator";
import { generateRisksammanstallningWord } from "@/lib/documentGenerator/risksammanstallningWordGenerator";
import {
  generateHandoverWordDocument,
  generateQaLogWordDocument,
} from "@/lib/documentGenerator/overlamningWordGenerator";
import {
  generateStagePlanWordDocument,
  generateParallelCoordinationWordDocument,
} from "@/lib/documentGenerator/samordningWordGenerator";
import { getAllQuestionsForExport } from "@/lib/documentGenerator/utils/exportQuestionBuilder";
import { fragorData } from "@/lib/fragorData";
import { skrFragorData } from "@/components/SKRBedomning/data/skrFragorData";
import type { HandoverKind } from "@/lib/basProcess/constants";
import type { RiskbedomningData } from "@/types/riskbedomning";
import type { SKRData } from "@/types/skr";

export type ExportPhase =
  | "01_Planering"
  | "02_Projektering"
  | "03_Utforande"
  | "04_Bruksskede";

export const PHASE_LABELS: Record<ExportPhase, string> = {
  "01_Planering": "Planering",
  "02_Projektering": "Projektering",
  "03_Utforande": "Utförande",
  "04_Bruksskede": "Bruksskede",
};

export interface ExportDocGroup {
  key: string;
  docNumber: string;
  title: string;
  phase: ExportPhase;
  count: number;
  /** Genererar dokumenten i gruppen. Anropas när capture-läget är aktivt. */
  run: () => Promise<void>;
}

const safeName = (value: string | null | undefined, fallback: string) =>
  (value || fallback).replace(/[^\p{L}\p{N}_-]+/gu, "_").slice(0, 60);

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const rows = async (
  table: string,
  projectId: string,
  select = "*"
): Promise<Record<string, any>[]> => {
  const { data } = await supabase
    .from(table as never)
    .select(select)
    .eq("project_id", projectId)
    .limit(1000);
  return (data as unknown as Record<string, any>[]) || [];
};

const childRows = async (
  table: string,
  fk: string,
  parentIds: string[]
): Promise<Record<string, Record<string, any>[]>> => {
  if (!parentIds.length) return {};
  const { data } = await supabase
    .from(table as never)
    .select("*")
    .in(fk, parentIds)
    .limit(5000);
  const grouped: Record<string, Record<string, any>[]> = {};
  ((data as unknown as Record<string, any>[]) || [])
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .forEach((row) => {
      const id = String(row[fk]);
      (grouped[id] ||= []).push(row);
    });
  return grouped;
};

/**
 * Läser projektets innehåll och bygger listan över vad som kan exporteras.
 */
export const collectExportGroups = async (
  projectId: string
): Promise<{ projectName: string | null; groups: ExportDocGroup[] }> => {
  const { data: projectRow } = await supabase
    .from("projects")
    .select("name")
    .eq("id", projectId)
    .maybeSingle();
  const projectName = (projectRow as { name?: string } | null)?.name ?? null;

  const [
    samRoutines,
    skrAssessments,
    plans,
    ampSections,
    workPreps,
    qaEntries,
    stagePlans,
    parallelDocs,
    safetyRounds,
    incidents,
    handovers,
    usePhase,
  ] = await Promise.all([
    rows("sam_routines", projectId),
    rows("skr_assessments", projectId),
    rows("work_environment_plans", projectId),
    rows("amp_sections", projectId),
    rows("work_preparations", projectId),
    rows("qa_log_entries", projectId),
    rows("stage_plans", projectId),
    rows("parallel_coordination_docs", projectId),
    rows("safety_rounds", projectId),
    rows("incident_reports", projectId),
    rows("handover_documents", projectId),
    rows("use_phase_followups", projectId),
  ]);

  const { data: riskData } = await supabase
    .from("folder_risk_assessments")
    .select("*")
    .eq("project_id", projectId)
    .is("deleted_at", null)
    .limit(1000);
  const riskAssessments = ((riskData as unknown as Record<string, any>[]) || []).filter(
    (r) => r.data
  );

  const roundItems = await childRows(
    "safety_round_items",
    "round_id",
    safetyRounds.map((r) => String(r.id))
  );
  const followupItems = await childRows(
    "use_phase_followup_items",
    "followup_id",
    usePhase.map((r) => String(r.id))
  );

  const groups: ExportDocGroup[] = [];

  groups.push({
    key: "sam",
    docNumber: "01",
    title: "Rutin systematiskt arbetsmiljöarbete",
    phase: "01_Planering",
    count: samRoutines.length,
    run: async () => {
      for (const r of samRoutines) {
        await generateSamRoutineWordDocument(
          {
            name: r.name,
            version: r.version,
            status: r.status,
            sections: (r.sections as Record<string, string>) || {},
          },
          projectName ?? undefined
        );
      }
    },
  });

  groups.push({
    key: "skr",
    docNumber: "03",
    title: "SKR-bedömning",
    phase: "01_Planering",
    count: skrAssessments.length,
    run: async () => {
      for (const a of skrAssessments) {
        const data = {
          ...((a.data as Record<string, unknown>) || {}),
          assessmentId: a.assessment_id,
          projektNamn: (a.data as any)?.projektNamn || a.name || projectName || "",
          svar: (a.data as any)?.svar || {},
        } as unknown as SKRData;
        await generateSKRWordDocument(data, skrFragorData);
      }
    },
  });

  const appointments = handovers.filter((h) =>
    ["utseende_basp", "utseende_basu"].includes(String(h.kind))
  );
  const transfers = handovers.filter(
    (h) => !["utseende_basp", "utseende_basu"].includes(String(h.kind))
  );

  groups.push({
    key: "utseende",
    docNumber: "05",
    title: "Uppdragsbekräftelse Bas-P och Bas-U",
    phase: "01_Planering",
    count: appointments.length,
    run: async () => {
      for (const h of appointments) {
        await generateHandoverWordDocument(
          h.name,
          h.kind as HandoverKind,
          (h.content_json as Record<string, Record<string, any>>) || {},
          projectName ?? undefined
        );
      }
    },
  });

  groups.push({
    key: "amp",
    docNumber: "12",
    title: "Arbetsmiljöplan",
    phase: "02_Projektering",
    count: plans.length,
    run: async () => {
      for (const plan of plans) {
        const sections = ampSections
          .filter((s) => !s.plan_id || s.plan_id === plan.id)
          .map((s) => ({
            section_key: s.section_key,
            title: s.title,
            status: s.status,
            body: s.body ?? null,
            na_motive: s.na_motive ?? null,
            legal_reference: s.legal_reference ?? null,
            sort_order: s.sort_order ?? 0,
            source_meta: (s.source_meta as any) ?? null,
            structured_data: (s.structured_data as Record<string, any>) || {},
          }));
        await generateAmpWordDocument(
          {
            name: plan.name,
            status: plan.status,
            version: plan.version,
            revision_note: plan.revision_note,
            revised_at: plan.revised_at,
            handover_at: plan.handover_at,
            handover_to: plan.handover_to,
            handover_note: plan.handover_note,
          },
          sections as never,
          projectName ?? undefined
        );
      }
    },
  });

  const utforandeRisk = riskAssessments.filter((r) => r.phase !== "bruksskede");
  const bruksRisk = riskAssessments.filter((r) => r.phase === "bruksskede");

  const runRisk = async (list: Record<string, any>[]) => {
    for (const r of list) {
      const data = {
        ...((r.data as Record<string, unknown>) || {}),
        projektNamn: (r.data as any)?.projektNamn || projectName || "",
        assessmentId: r.assessment_id,
        svar: (r.data as any)?.svar || {},
      } as unknown as RiskbedomningData;
      await generateRisksammanstallningWord(
        data,
        getAllQuestionsForExport(data, fragorData)
      );
    }
  };

  groups.push({
    key: "risk_utforande",
    docNumber: "30",
    title: "Risksammanställning utförande",
    phase: "02_Projektering",
    count: utforandeRisk.length,
    run: () => runRisk(utforandeRisk),
  });

  groups.push({
    key: "risk_bruk",
    docNumber: "31",
    title: "Risksammanställning bruksskede",
    phase: "04_Bruksskede",
    count: bruksRisk.length,
    run: () => runRisk(bruksRisk),
  });

  groups.push({
    key: "qa",
    docNumber: "25",
    title: "Q&A-logg",
    phase: "02_Projektering",
    count: qaEntries.length ? 1 : 0,
    run: async () => {
      if (!qaEntries.length) return;
      await generateQaLogWordDocument(qaEntries as never, projectName ?? undefined);
    },
  });

  groups.push({
    key: "stage_plans",
    docNumber: "26",
    title: "Skedesplan",
    phase: "02_Projektering",
    count: stagePlans.length,
    run: async () => {
      for (const p of stagePlans) {
        await generateStagePlanWordDocument(
          p.name,
          (p.content_json as Record<string, any[]>) || {},
          projectName ?? undefined
        );
      }
    },
  });

  groups.push({
    key: "parallel",
    docNumber: "26",
    title: "Parallell projektering och produktion",
    phase: "02_Projektering",
    count: parallelDocs.length,
    run: async () => {
      for (const p of parallelDocs) {
        await generateParallelCoordinationWordDocument(
          p.name,
          (p.content_json as Record<string, any[]>) || {},
          projectName ?? undefined
        );
      }
    },
  });

  groups.push({
    key: "arbetsberedningar",
    docNumber: "24",
    title: "Arbetsberedningar",
    phase: "03_Utforande",
    count: workPreps.length,
    run: async () => {
      for (const wp of workPreps) {
        const payload = (wp.data as Record<string, any>) || {};
        await generateArbetsberedningWordDocument({
          id: wp.id,
          projektId: wp.project_id,
          folderId: wp.folder_id ?? undefined,
          name: wp.name,
          reviewStatus: wp.review_status,
          reviewComment: wp.review_comment ?? undefined,
          reviewedAt: wp.reviewed_at ?? undefined,
          submittedAt: wp.submitted_at ?? undefined,
          status: wp.status,
          createdAt: wp.created_at,
          updatedAt: wp.updated_at,
          ...payload,
          fields: payload.fields || {},
          project_name: projectName,
        } as never);
      }
    },
  });

  groups.push({
    key: "skyddsronder",
    docNumber: "27",
    title: "Skyddsronder",
    phase: "03_Utforande",
    count: safetyRounds.length,
    run: async () => {
      for (const round of safetyRounds) {
        await generateSafetyRoundWordDocument(
          {
            name: round.name,
            round_type: round.round_type,
            round_date: round.round_date,
            area: round.area ?? round.area_name,
            participants: round.participants,
            status: round.status,
            notes: round.notes,
            items: (roundItems[String(round.id)] || []).map((i) => ({
              section: i.section,
              control_point: i.control_point,
              legal_reference: i.legal_reference,
              status: i.status,
              responsible: i.responsible,
              due_date: i.due_date,
              comment: i.comment,
            })),
          } as never,
          projectName ?? undefined
        );
      }
    },
  });

  groups.push({
    key: "tillbud",
    docNumber: "28",
    title: "Tillbud och olyckor",
    phase: "03_Utforande",
    count: incidents.length,
    run: async () => {
      for (const report of incidents) {
        await generateIncidentReportWordDocument(report as never, projectName ?? undefined);
      }
    },
  });

  groups.push({
    key: "overlamningar",
    docNumber: "20/22/34",
    title: "Överlämningar",
    phase: "03_Utforande",
    count: transfers.length,
    run: async () => {
      for (const h of transfers) {
        await generateHandoverWordDocument(
          h.name,
          h.kind as HandoverKind,
          (h.content_json as Record<string, Record<string, any>>) || {},
          projectName ?? undefined
        );
      }
    },
  });

  groups.push({
    key: "bruksuppfoljning",
    docNumber: "33",
    title: "Uppföljning bruksskede",
    phase: "04_Bruksskede",
    count: usePhase.length,
    run: async () => {
      for (const f of usePhase) {
        await generateUsePhaseFollowupWordDocument(
          {
            space_name: f.space_name,
            description: f.description,
            status: f.status,
            items: (followupItems[String(f.id)] || []).map((i) => ({
              section: i.section,
              control_point: i.control_point,
              legal_reference: i.legal_reference,
              status: i.status,
              responsible: i.responsible,
              checked_at: i.checked_at,
              comment: i.comment,
            })),
          } as never,
          projectName ?? undefined
        );
      }
    },
  });

  return { projectName, groups };
};

export interface FullExportProgress {
  current: number;
  total: number;
  label: string;
}

export interface FullExportResult {
  fileCount: number;
  skipped: string[];
  failed: string[];
}

const buildIndexText = (
  projectName: string | null,
  included: { group: ExportDocGroup; files: CapturedExportFile[] }[],
  missing: ExportDocGroup[],
  failed: string[]
): string => {
  const lines: string[] = [
    "Innehållsförteckning – samlad dokumentnedladdning",
    "Byggarbetsmiljökontrollen",
    "",
    `Projekt: ${projectName ?? "Namnlöst projekt"}`,
    `Skapad: ${new Date().toLocaleString("sv-SE")}`,
    "",
    "INGÅR I PAKETET",
  ];
  included.forEach(({ group, files }) => {
    lines.push(
      `  Dok ${group.docNumber} · ${group.title} (${PHASE_LABELS[group.phase]}) – ${files.length} fil(er)`
    );
    files.forEach((f) => lines.push(`      ${f.filename}`));
  });
  if (!included.length) lines.push("  (inga dokument)");

  lines.push("", "SAKNAS – INGET UNDERLAG REGISTRERAT");
  if (missing.length) {
    missing.forEach((g) =>
      lines.push(`  Dok ${g.docNumber} · ${g.title} (${PHASE_LABELS[g.phase]})`)
    );
  } else {
    lines.push("  (inget saknas)");
  }

  if (failed.length) {
    lines.push("", "KUNDE INTE GENERERAS");
    failed.forEach((f) => lines.push(`  ${f}`));
  }

  lines.push(
    "",
    "Underlag/ innehåller all rådata som JSON och CSV.",
    "Varje Word-fil är versionsmärkt och loggad i exporthistoriken."
  );
  return lines.join("\n");
};

/**
 * Genererar valda dokument som Word-filer och laddar ner allt som en ZIP.
 */
export const downloadAllDocuments = async (
  projectId: string,
  options: {
    groups: ExportDocGroup[];
    selectedKeys: string[];
    projectName: string | null;
    includeRawData?: boolean;
    onProgress?: (progress: FullExportProgress) => void;
  }
): Promise<FullExportResult> => {
  const { groups, selectedKeys, projectName, includeRawData = true, onProgress } = options;
  const selected = groups.filter((g) => selectedKeys.includes(g.key) && g.count > 0);
  const missing = groups.filter((g) => g.count === 0);
  const total = selected.length + (includeRawData ? 1 : 0);

  const zip = new JSZip();
  const included: { group: ExportDocGroup; files: CapturedExportFile[] }[] = [];
  const failed: string[] = [];
  let fileCount = 0;
  let step = 0;

  for (const group of selected) {
    step += 1;
    onProgress?.({ current: step, total, label: `Dok ${group.docNumber} · ${group.title}` });
    beginExportCapture();
    try {
      await group.run();
    } catch (err) {
      failed.push(`Dok ${group.docNumber} ${group.title}: ${(err as Error).message}`);
    }
    const files = endExportCapture();
    if (!files.length) continue;

    const folder = zip.folder(group.phase);
    files.forEach((file, index) => {
      const suffix = files.length > 1 ? `_${index + 1}` : "";
      const name = `${group.docNumber.replace(/\//g, "-")}_${safeName(
        file.documentName || group.title,
        group.title
      )}${suffix}.docx`;
      folder?.file(name, file.blob);
      fileCount += 1;
    });
    included.push({ group, files });
  }

  if (includeRawData) {
    step += 1;
    onProgress?.({ current: step, total, label: "Underlag (JSON och CSV)" });
    try {
      const bundle = await collectProjectBundle(projectId);
      const raw = zip.folder("Underlag");
      raw?.file("projekt.json", JSON.stringify(bundle, null, 2));
      Object.entries(bundle.tables).forEach(([table, tableRows]) => {
        if (!tableRows.length) return;
        raw?.file(`${table}.csv`, rowsToCsv(tableRows));
      });
    } catch (err) {
      failed.push(`Underlag: ${(err as Error).message}`);
    }
  }

  zip.file(
    "00_Innehallsforteckning.txt",
    buildIndexText(projectName, included, missing, failed)
  );

  const blob = await zip.generateAsync({ type: "blob" });
  triggerDownload(
    blob,
    `Alla_dokument_${safeName(projectName, projectId)}_${new Date()
      .toISOString()
      .slice(0, 10)}.zip`
  );

  void trackUsage("project_exported", {
    projectId,
    metadata: { fileCount, groups: included.length },
  });

  return {
    fileCount,
    skipped: missing.map((g) => `Dok ${g.docNumber} ${g.title}`),
    failed,
  };
};
